import { Bot, File, User, type LucideIcon } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Discriminating literal union for the domain glyph. Drives the underlying
 * `lucide-react` component selection. Add a key here only when the Figma
 * registry adds a new domain glyph.
 */
export type DomainIconName = 'files' | 'humanIdentity' | 'nonHumanIdentity' | 'externalIdentity';

/**
 * Sizes published in Figma per TDEV-273 design.md, plus `12` — the Access
 * Discovery popovers instance the glyphs at 12x12 (Figma `5:20347`
 * "Files Icon 12X12", `5:20351` "HI Icon 12X12").
 */
export type DomainIconSize = 12 | 14 | 16 | 18 | 24 | 28;

export interface DomainIconProps {
  name: DomainIconName;
  size: DomainIconSize;
  className?: string;
  /** Optional aria-label. When omitted, the glyph is decorative (`aria-hidden`). */
  'aria-label'?: string;
}

/**
 * Map name → lucide component. `as const satisfies Readonly<Record<…>>` proves
 * exhaustive coverage of the literal union under `noUncheckedIndexedAccess`,
 * making the lookup return `LucideIcon` (not `LucideIcon | undefined`).
 */
// `externalIdentity` shares the plain `User` glyph with `humanIdentity` per
// Figma `5:23254` — semantic differentiation is by color, not glyph shape.
const NAME_TO_GLYPH = {
  files: File,
  humanIdentity: User,
  nonHumanIdentity: Bot,
  externalIdentity: User,
} as const satisfies Readonly<Record<DomainIconName, LucideIcon>>;

/**
 * DomainIcon — files / humanIdentity / nonHumanIdentity / externalIdentity at
 * 12 / 14 / 16 / 18 / 24 / 28 px per Figma registry. Thin wrapper
 * around `lucide-react` glyphs.
 */
export const DomainIcon: React.FC<DomainIconProps> = ({
  name,
  size,
  className,
  'aria-label': ariaLabel,
}) => {
  const Glyph = NAME_TO_GLYPH[name];
  return (
    <Glyph
      width={size}
      height={size}
      className={cn(className)}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    />
  );
};

DomainIcon.displayName = 'DomainIcon';
